import { ATTENDANCE_RULES } from '@/constant/hr/attendance-rule-catalog';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { blocksClient } from '@/lib/blocks/client';
import { getIamUserOrganizationAccess } from './employee-account.service';

/**
 * Whether lunch / overtime / breaks are on for an employee. This is decided by Blocks IAM, not by a
 * profile flag or the employee's region: it's on when the employee holds `HRM_PERMISSIONS.PROFILE.
 * LUNCH_ENABLED` / `OVERTIME_ENABLED` / one of `BREAK_PERMISSIONS` through one of their roles. The
 * signed-in user's own permissions come from the token; for anybody else they are resolved here
 * from the two sides IAM exposes — the roles each permission is assigned to, and the roles each
 * user holds.
 */
export interface WorkStatusEligibility {
  lunch: boolean;
  overtime: boolean;
  breaks: boolean;
}

export interface EligibilityRoles {
  lunch: string[];
  overtime: string[];
  breaks: string[];
}

/** The permissions that let an employee take a break: regular breaks, or any lunch rule. */
export const BREAK_PERMISSIONS = [
  HRM_PERMISSIONS.ATTENDANCE.TAKE_BREAK,
  ...ATTENDANCE_RULES.filter((rule) => rule.group === 'lunch').map((rule) => rule.permission),
];

type IamPermission = { resource?: string; roles?: unknown; isArchived?: boolean };

const stringsOf = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

/** The live (not archived) permissions of one IAM resource group. */
const listPermissions = async (resourceGroup: string): Promise<IamPermission[]> => {
  const response = (await blocksClient.iam.permissions.list({
    filter: { resourceGroup },
    page: 0,
    pageSize: 100,
  })) as { data?: IamPermission[] } | undefined;
  return (response?.data ?? []).filter((permission) => !permission.isArchived);
};

/** The roles each permission is assigned to (an IAM permission lists its roles). */
export const getEligibilityRoles = async (): Promise<EligibilityRoles> => {
  const [profile, attendance] = await Promise.all([
    listPermissions('profile'),
    listPermissions('attendance'),
  ]);
  const rolesOf = (permissions: IamPermission[], resource: string) =>
    stringsOf(permissions.find((permission) => permission.resource === resource)?.roles);
  return {
    lunch: rolesOf(profile, HRM_PERMISSIONS.PROFILE.LUNCH_ENABLED),
    overtime: rolesOf(profile, HRM_PERMISSIONS.PROFILE.OVERTIME_ENABLED),
    breaks: [...new Set(BREAK_PERMISSIONS.flatMap((resource) => rolesOf(attendance, resource)))],
  };
};

/** The user's roles in the given organization, or in every organization when it isn't known. */
export const getUserRolesInOrganization = async (
  userId: string,
  organizationId?: string
): Promise<string[]> => {
  const { organizationAccess } = await getIamUserOrganizationAccess(userId);
  const current = organizationId
    ? organizationAccess.find((entry) => entry.organizationId === organizationId)
    : undefined;
  return current ? current.roles : [...new Set(organizationAccess.flatMap((entry) => entry.roles))];
};

export const resolveWorkStatusEligibility = (
  userRoles: readonly string[],
  eligibilityRoles: EligibilityRoles
): WorkStatusEligibility => ({
  lunch: userRoles.some((role) => eligibilityRoles.lunch.includes(role)),
  overtime: userRoles.some((role) => eligibilityRoles.overtime.includes(role)),
  breaks: userRoles.some((role) => eligibilityRoles.breaks.includes(role)),
});
